<script lang="ts">
  /**
   * Settings: a full page of grouped rows, and at its foot the build this is.
   *
   * - **`groups`**: the game's settings, as the shell's parts: `Group`s of
   *   `ToggleRow` (sound, vibration), `SliderRow` (a volume, under its
   *   toggle or on its own), `Row` with a `SegmentedControl` as `trailing`
   *   (language, appearance), `Row` with `under` for a set of `Chip`s or a
   *   `Stepper`, `Row` with `chevron` for a page of its own (how to play,
   *   about, the account). A heading between groups is a `SectionLabel
   *   variant="sheet"`, a sentence under one a `GroupNote`.
   * - **`buildId`** (required): the build, set in the About footer under the
   *   last group ("Build 4a8525e"). A deploy that greps the served bundle
   *   for its commit finds it here, and a player reading it to a developer
   *   reads the same value. `formatBuild` words it (default
   *   `Build <id>`).
   *
   * `frame` (`pageTypes.ts`): `page` (the default) is a `FullPage` in the
   * `done` dress, 40rem wide from 1024px; `screen` is the game's own routed
   * screen under a back chevron.
   *
   * **`frame="prompt"`** (since 1.7.0): a first-run question with one way on
   * and no way out ("Make it yours": the appearance and the accent, before
   * the first game). The same page, but:
   *
   * - no way out is drawn, and Escape and Back do not close it (a
   *   non-dismissible `FullPage`): each calls `onbackattempt` with which it
   *   was, if the game wants to say something; `onclose` is never called;
   * - `lead`, a sentence under the title over the groups, says what is asked
   *   and describes the dialog (`aria-describedby`), so a screen reader
   *   reads the question with the title when focus lands on the page;
   * - **`primary`** (required there, by the props' type) is the one way on,
   *   a filled pill pinned to the bottom (Continue). It is the page's only
   *   filled button and its only exit: everything above it can be changed
   *   later in Settings, which the game says in `lead`. **Its `disabled`
   *   must never stay `true`** (nor `busy`, for long): a disabled one way on
   *   with no way out traps the player. Disable it only for a moment (while
   *   an answer saves), and never wait on the network to enable it;
   * - no build line (the page is not Settings; `buildId` is still passed,
   *   and is drawn on the Settings page proper).
   *
   * A prompt that arrives without `primary` anyway (an untyped caller) is
   * never a trap: it is drawn as the `page` frame, dismissible (Done, Escape
   * and Back call `onclose`), and the console says so once.
   *
   * Test ids: `testid` on the dialog or screen; `testids.done` /
   * `testids.back` on the way out; `testids.build` on the footer's line
   * (default `ui-settings-build`); in the prompt, `primary.testid` (default
   * `ui-settings-primary`) and `testids.lead` (default `ui-settings-lead`).
   */
  import type { Snippet } from 'svelte';

  import PillButton from '../components/PillButton.svelte';
  import { shellText } from '../i18n.svelte.js';
  import type { SheetBackAttempt } from '../types.js';
  import PageFrame from './PageFrame.svelte';
  import type { PageDress, SettingsPageFrameProps, SettingsPromptProps } from './pageTypes.js';

  // `frame: 'prompt'` requires `primary` (1.7.0): one way on, always.
  type Props = SettingsProps & (SettingsPageFrameProps | SettingsPromptProps);

  interface SettingsProps {
    /** The page's title and accessible name ("Settings"). */
    readonly title: string;
    readonly onclose: () => void;
    /** The settings, as groups of rows. */
    readonly groups: Snippet;
    /** The build (a commit id). Always rendered (but in the `prompt` frame). */
    readonly buildId: string;
    /** The footer's words around the id. Default the shell's `build` ("Build <id>"). */
    readonly formatBuild?: ((buildId: string) => string) | undefined;
    readonly open?: boolean;
    readonly dress?: PageDress;
    readonly leaveLabel?: string | undefined;
    /** 40rem from 1024px (`page` frame). Default true. */
    readonly wide?: boolean;
    readonly testid?: string | undefined;
    readonly testids?:
      | {
          readonly done?: string;
          readonly back?: string;
          readonly build?: string;
          readonly lead?: string;
        }
      | undefined;
    // ── 1.7.0: the prompt frame ──
    /** `prompt`: the sentence under the title that says what is asked. */
    readonly lead?: string | undefined;
    /** `prompt`: Escape or Back, which do not close it. */
    readonly onbackattempt?: ((reason: SheetBackAttempt) => void) | undefined;
  }
  const {
    title,
    onclose,
    groups,
    buildId,
    formatBuild,
    open = true,
    frame = 'page',
    dress,
    leaveLabel,
    wide = true,
    testid,
    testids,
    lead,
    primary,
    onbackattempt,
  }: Props = $props();

  const uid = $props.id();
  // A prompt without its one way on is drawn as a dismissible page, never a trap.
  const prompt = $derived(frame === 'prompt' && primary !== undefined);
  const buildLine = $derived(
    formatBuild === undefined ? shellText('build', { id: buildId }) : formatBuild(buildId),
  );

  let warned = false;
  $effect(() => {
    if (warned || frame !== 'prompt' || primary !== undefined) return;
    warned = true;
    console.warn(
      'SettingsPage: the prompt frame needs `primary`, its one way on; drawn as a dismissible page.',
    );
  });
</script>

{#snippet promptFooter()}
  {#if primary !== undefined}
    <PillButton
      label={primary.label}
      size="lg"
      disabled={primary.disabled === true}
      busy={primary.busy === true}
      icon={primary.icon}
      testid={primary.testid ?? 'ui-settings-primary'}
      onclick={primary.onclick}
    />
  {/if}
{/snippet}

<PageFrame
  frame={frame === 'prompt' ? 'page' : frame}
  {open}
  {title}
  {onclose}
  dress={dress ?? (frame === 'screen' ? 'chevron' : 'done')}
  {wide}
  {leaveLabel}
  measure="narrow"
  footer={prompt ? promptFooter : undefined}
  dismissible={!prompt}
  {onbackattempt}
  describedby={prompt && lead !== undefined ? `${uid}-lead` : undefined}
  {testid}
  {testids}
>
  {#if prompt && lead !== undefined}
    <p class="ui-settings-lead" id="{uid}-lead" data-testid={testids?.lead ?? 'ui-settings-lead'}>
      {lead}
    </p>
  {/if}
  <div class="ui-settings-groups">{@render groups()}</div>
  {#if !prompt}
    <footer class="ui-settings-about">
      <p class="ui-settings-build" data-testid={testids?.build ?? 'ui-settings-build'}>
        {buildLine}
      </p>
    </footer>
  {/if}
</PageFrame>

<style>
  .ui-settings-groups {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .ui-settings-about {
    margin-top: var(--ui-space-2);
    padding: 0 0.25rem;
  }

  .ui-settings-build {
    margin: 0;
    text-align: center;
    font: var(--ui-text-caption);
    color: var(--ui-ink-2);
  }

  /* The question (1.7.0): under the title, over the groups. */
  .ui-settings-lead {
    margin: 0 0 var(--ui-space-4);
    padding: 0 0.25rem;
    font: var(--ui-text-body);
    line-height: 1.4;
    color: var(--ui-ink-2);
  }
</style>
